import Bitmap from "../../bitmap";
import Color from "../../models/color";
import { BaseBlockPack } from "./BaseBlockPack";

export default class GlassBlockPack extends BaseBlockPack {
  drawBlock(
    bit: Bitmap,
    x: number,
    y: number,
    side: number,
    width: number,
    color: Color,
    to: number[]
  ): void {
    this.drawRawBlock(bit, x, y, side, width, color);
    // No corner connections needed for glass - it's a single pane
  }

  private drawRawBlock(
    bit: Bitmap,
    x: number,
    y: number,
    side: number,
    width: number,
    color: Color
  ): void {
    const ctx = bit.context;
    const px = Math.round(x);
    const py = Math.round(y);
    const size = width;

    // Determine neighbors from side flags
    const hasTop = !(side & 2);
    const hasBottom = !(side & 8);
    const hasLeft = !(side & 1);
    const hasRight = !(side & 4);

    ctx.save();

    // 1. Base transparent fill (the actual "glass")
    // Very transparent color - this is where transparency comes from
    const baseRgb = this.getColorRgb(color);
    const baseAlpha = 0.2; // 0.10-0.18 range
    ctx.fillStyle = `rgba(${baseRgb.r}, ${baseRgb.g}, ${baseRgb.b}, ${baseAlpha})`;
    ctx.fillRect(px, py, size, size);

    // 2. Edge-intensified gradient (subtle - less stripes)
    // Only top and left edges for a lighter look
    // Top fade (subtle)
    const topFadeHeight = size * 0.2;
    const topGradient = ctx.createLinearGradient(px, py, px, py + topFadeHeight);
    topGradient.addColorStop(0, `rgba(${baseRgb.r}, ${baseRgb.g}, ${baseRgb.b}, 0.15)`);
    topGradient.addColorStop(1, `rgba(${baseRgb.r}, ${baseRgb.g}, ${baseRgb.b}, 0.00)`);
    ctx.fillStyle = topGradient;
    ctx.fillRect(px, py, size, topFadeHeight);

    // Left fade (subtle) - for top-left highlight effect
    const leftFadeWidth = size * 0.2;
    const leftGradient = ctx.createLinearGradient(px, py, px + leftFadeWidth, py);
    leftGradient.addColorStop(0, `rgba(${baseRgb.r}, ${baseRgb.g}, ${baseRgb.b}, 0.12)`);
    leftGradient.addColorStop(1, `rgba(${baseRgb.r}, ${baseRgb.g}, ${baseRgb.b}, 0.00)`);
    ctx.fillStyle = leftGradient;
    ctx.fillRect(px, py, leftFadeWidth, size);

    // 3. Outer colored glass border (rim) - only on exposed edges
    const rimRgb = this.saturateColor(baseRgb, 0.3);
    const rimAlpha = 0.60; // Reduced shine (was 0.70)
    const rimThickness = 2;

    ctx.strokeStyle = `rgba(${rimRgb.r}, ${rimRgb.g}, ${rimRgb.b}, ${rimAlpha})`;
    ctx.lineWidth = rimThickness;
    ctx.lineCap = "square";

    // Top edge (if exposed)
    if (!hasTop) {
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(px + size, py);
      ctx.stroke();
    }

    // Bottom edge (if exposed)
    if (!hasBottom) {
      ctx.beginPath();
      ctx.moveTo(px, py + size);
      ctx.lineTo(px + size, py + size);
      ctx.stroke();
    }

    // Left edge (if exposed)
    if (!hasLeft) {
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(px, py + size);
      ctx.stroke();
    }

    // Right edge (if exposed)
    if (!hasRight) {
      ctx.beginPath();
      ctx.moveTo(px + size, py);
      ctx.lineTo(px + size, py + size);
      ctx.stroke();
    }

    // Optional inner rim (offset inward by 1px)
    const innerAlpha = 0.80; // Reduced shine (was 0.40)
    ctx.strokeStyle = `rgba(255, 255, 255, ${innerAlpha})`;
    ctx.lineWidth = 1;

    if (!hasTop) {
      ctx.beginPath();
      ctx.moveTo(px + 1, py + 1);
      ctx.lineTo(px + size - 1, py + 1);
      ctx.stroke();
    }

    if (!hasBottom) {
      ctx.beginPath();
      ctx.moveTo(px + 1, py + size - 1);
      ctx.lineTo(px + size - 1, py + size - 1);
      ctx.stroke();
    }

    if (!hasLeft) {
      ctx.beginPath();
      ctx.moveTo(px + 1, py + 1);
      ctx.lineTo(px + 1, py + size - 1);
      ctx.stroke();
    }

    if (!hasRight) {
      ctx.beginPath();
      ctx.moveTo(px + size - 1, py + 1);
      ctx.lineTo(px + size - 1, py + size - 1);
      ctx.stroke();
    }

    // 4. Global highlight & shadow (directional lighting)
    // 4.1 Top highlight - reduced shine
    if (!hasTop) {
      const hlAlpha = 0.10; // Reduced shine (was 0.70)
      ctx.strokeStyle = `rgba(255, 255, 255, ${hlAlpha})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(px + size, py);
      ctx.stroke();

      // Tiny vertical gradient just under that line (1-2px tall)
      const gradientHeight = Math.max(1, Math.min(2, Math.floor(size / 10)));
      const hlGradient = ctx.createLinearGradient(px, py, px, py + gradientHeight);
      hlGradient.addColorStop(0, `rgba(255, 255, 255, 0.40)`); // Reduced (was 0.50)
      hlGradient.addColorStop(1, `rgba(255, 255, 255, 0.00)`);
      ctx.fillStyle = hlGradient;
      ctx.fillRect(px, py, size, gradientHeight);
    }

    // 4.3 Top-left piece highlight - ONLY on the top-left block of the piece
    // Only add highlight if this block has no top neighbor AND no left neighbor
    // This means it's at the top-left corner of the piece
    // Make it an internal specular highlight, not a glow
    if (!hasTop && !hasLeft) {
      // Internal specular highlight - contained within the block
      const highlightSize = size * 0.4; // Smaller, more contained
      const highlightX = px + size * 0.15;
      const highlightY = py + size * 0.15;
      const topLeftGradient = ctx.createRadialGradient(
        highlightX, highlightY, 0,
        highlightX, highlightY, highlightSize
      );
      topLeftGradient.addColorStop(0, `rgba(255, 255, 255, 0.4)`); // Bright center
      topLeftGradient.addColorStop(0.6, `rgba(255, 255, 255, 0.15)`); // Fade
      topLeftGradient.addColorStop(1, `rgba(255, 255, 255, 0.00)`); // Transparent edge
      ctx.fillStyle = topLeftGradient;
      ctx.beginPath();
      // Clip to block bounds
      ctx.rect(px, py, size, size);
      ctx.clip();
      ctx.beginPath();
      ctx.arc(highlightX, highlightY, highlightSize, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore(); // Restore clipping
      ctx.save(); // Save again for remaining operations
    }

    // 4.2 Bottom shadow
    if (!hasBottom) {
      const shadowRgb = this.darkenColor(baseRgb, 0.25);
      const shAlpha = 0.35;
      ctx.strokeStyle = `rgba(${shadowRgb.r}, ${shadowRgb.g}, ${shadowRgb.b}, ${shAlpha})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(px, py + size);
      ctx.lineTo(px + size, py + size);
      ctx.stroke();
    }

    // 5. Internal seams (very subtle - less stripes)
    // Only draw seams where tiles are connected (has neighbor)
    const seamAlpha = 0.04; // Reduced from 0.08 for less visible stripes
    ctx.strokeStyle = `rgba(255, 255, 255, ${seamAlpha})`;
    ctx.lineWidth = 1;

    // Right seam (if has right neighbor) - very subtle
    if (hasRight) {
      ctx.beginPath();
      ctx.moveTo(px + size, py);
      ctx.lineTo(px + size, py + size);
      ctx.stroke();
    }

    // Bottom seam (if has bottom neighbor) - very subtle
    if (hasBottom) {
      ctx.beginPath();
      ctx.moveTo(px, py + size);
      ctx.lineTo(px + size, py + size);
      ctx.stroke();
    }

    ctx.restore();
  }

  /**
   * Gets RGB values from a Color object
   */
  private getColorRgb(color: Color): { r: number; g: number; b: number } {
    // color.shade() returns rgba(R,G,B,255), so we need to parse it
    // Or we can access the palette directly
    const pal = color.palette.values[color.base];
    return {
      r: pal.r,
      g: pal.g,
      b: pal.b
    };
  }

  /**
   * Darkens a color by a factor (0.0 = no change, 1.0 = black)
   */
  private darkenColor(rgb: { r: number; g: number; b: number }, factor: number): { r: number; g: number; b: number } {
    return {
      r: Math.round(rgb.r * (1 - factor)),
      g: Math.round(rgb.g * (1 - factor)),
      b: Math.round(rgb.b * (1 - factor))
    };
  }

  /**
   * Saturates a color (increases saturation)
   * factor: 0.0 = no change, 1.0 = fully saturated
   */
  private saturateColor(rgb: { r: number; g: number; b: number }, factor: number): { r: number; g: number; b: number } {
    // Convert to HSL, increase saturation, convert back
    // Simplified: increase the difference between max and min channels
    const max = Math.max(rgb.r, rgb.g, rgb.b);
    const min = Math.min(rgb.r, rgb.g, rgb.b);
    const delta = max - min;
    
    if (delta === 0) return rgb; // Grayscale, no change
    
    const newDelta = delta * (1 + factor);
    const scale = newDelta / delta;
    
    // Apply scaling to channels relative to the average
    const avg = (rgb.r + rgb.g + rgb.b) / 3;
    
    return {
      r: Math.round(Math.max(0, Math.min(255, avg + (rgb.r - avg) * scale))),
      g: Math.round(Math.max(0, Math.min(255, avg + (rgb.g - avg) * scale))),
      b: Math.round(Math.max(0, Math.min(255, avg + (rgb.b - avg) * scale)))
    };
  }
}
